import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const GeneratedContentContext = createContext();

const STORAGE_KEY = "quizmate_generated_content";

export const GeneratedContentProvider = ({ children }) => {
  const [generatedContent, setGeneratedContent] = useState([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setGeneratedContent(parsed);
      }
    } catch (error) {
      console.error("Error loading generated content:", error);
    }
  }, []);

  // Save to localStorage whenever content changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(generatedContent));
    } catch (error) {
      console.error("Error saving generated content:", error);
    }
  }, [generatedContent]);

  const saveGeneratedContent = useCallback((content) => {
    const newItem = {
      id: `gen-${Date.now()}`,
      name: content.name,
      type: content.type, // 'notes', 'quotes', 'quiz', 'flashcards'
      documentName: content.documentName,
      data: content.data,
      createdAt: new Date().toISOString(),
    };

    setGeneratedContent((prev) => [newItem, ...prev].slice(0, 50)); // Keep max 50 items
    return newItem.id;
  }, []);

  const deleteGeneratedContent = useCallback((id) => {
    setGeneratedContent((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const renameGeneratedContent = useCallback((id, newName) => {
    setGeneratedContent((prev) =>
      prev.map((item) => (item.id === id ? { ...item, name: newName } : item))
    );
  }, []);

  const getGeneratedContent = useCallback(
    (id) => {
      return generatedContent.find((item) => item.id === id);
    },
    [generatedContent]
  );

  const clearAllGeneratedContent = useCallback(() => {
    setGeneratedContent([]);
  }, []);

  const value = {
    generatedContent,
    saveGeneratedContent,
    deleteGeneratedContent,
    renameGeneratedContent,
    getGeneratedContent,
    clearAllGeneratedContent,
  };

  return (
    <GeneratedContentContext.Provider value={value}>
      {children}
    </GeneratedContentContext.Provider>
  );
};

export const useGeneratedContent = () => {
  const context = useContext(GeneratedContentContext);
  if (!context) {
    throw new Error(
      "useGeneratedContent must be used within a GeneratedContentProvider"
    );
  }
  return context;
};
